/**
 * useGridLayout — resolves the active grid layout config.
 *
 * Resolution order:
 *   1. If gridLayout localStorage = 'default' → null (legacy JSX)
 *   2. Custom grid-layout from API (Phase 2 ✓)
 *   3. Grid layout selection from Appearance panel (hero-sidebar, magazine, stack)
 *   4. Template matching the current layoutStyle
 *   5. null → fallback to legacy JSX layout
 *
 * The hook only returns a config when:
 *   - The ENABLE_GRID_ENGINE feature flag is on
 *   - A matching template or custom layout exists
 */

import { useState, useEffect, useMemo } from 'react'
import { isGridEngineEnabled } from '../config/featureFlags'
import { getTemplate, redistributeSections } from '../config/gridTemplates'
import { validateGridLayout, migrateConfig } from '../config/gridSchema'
import { getConfig } from '../config/environment'
import api, { apiRoutes } from '../api'
import { hasAuthorizedContentAdminToken } from '../utils/adminToken'
import { useOptionalDraft } from '../contexts/DraftContext'

const GRID_LAYOUT_KEY = 'gridLayout'

const DEDICATED_JSX_LAYOUT_STYLES = new Set(['v2'])

/**
 * @param {string} layoutStyle — current layout style ID (e.g. 'standard', 'premium-plus')
 * @param {string} gameType — current game type (e.g. 'classic5050')
 * @param {Object} options — optional configuration
 * @param {boolean} options.editMode — if true, fetch draft layouts for admin preview
 * @returns {{ gridConfig: Object|null, isGridEngine: boolean, errors: string[], loading: boolean }}
 */
export function useGridLayout(layoutStyle, gameType, { editMode = false } = {}) {
  const draft = useOptionalDraft()
  const { tlfId } = getConfig()
  const isAdminPreview = editMode || hasAuthorizedContentAdminToken(tlfId)
  const [customLayout, setCustomLayout] = useState(null)
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(false)
  const getPublishedGridLayout = () => (
    typeof window !== 'undefined' ? window.SITE_CONFIG?.defaults?.grid_layout : null
  ) || 'default'

  const [gridLayoutSelection, setGridLayoutSelection] = useState(() => {
    if (isAdminPreview) {
      return draft?.getDraftAppearance?.('gridLayout') || localStorage.getItem(GRID_LAYOUT_KEY) || getPublishedGridLayout()
    }
    return getPublishedGridLayout()
  })

  const enabled = isGridEngineEnabled()

  // Listen for gridLayoutChange events from AdminStyleSelector
  useEffect(() => {
    const handler = (e) => setGridLayoutSelection(e.detail)
    window.addEventListener('gridLayoutChange', handler)
    return () => window.removeEventListener('gridLayoutChange', handler)
  }, [])

  useEffect(() => {
    if (!isAdminPreview) return undefined
    const handler = (event) => {
      const next = event?.detail?.appearance?.gridLayout
      if (typeof next === 'string' && next) {
        setGridLayoutSelection(next)
      }
    }
    window.addEventListener('appearanceDraftSync', handler)
    return () => window.removeEventListener('appearanceDraftSync', handler)
  }, [isAdminPreview])

  useEffect(() => {
    const handler = () => {
      setGridLayoutSelection(getPublishedGridLayout())
    }
    window.addEventListener('appearanceReset', handler)
    return () => window.removeEventListener('appearanceReset', handler)
  }, [])

  // Phase 2: fetch custom layout from API. Public users only request published
  // layouts; admin edit mode can include unpublished drafts for preview.
  useEffect(() => {
    if (!enabled) return
    if (gridLayoutSelection === 'default') {
      setCustomLayout(null)
      return
    }

    const fetchCustomLayout = async () => {
      setLoading(true)
      setApiError(false)

      try {
        const { tlfId } = getConfig()
        const params = {
          organizationId: tlfId,
          gameType: gameType,
        }

        if (editMode && hasAuthorizedContentAdminToken(tlfId)) {
          params.includeDrafts = true
        } else {
          params.publishedOnly = true
        }

        const response = await api.get(apiRoutes.GridLayoutPublic, { params })
        
        // Handle backend OperationResult structure
        const payload = response?.data?.payload || response?.data
        const layouts = payload?.layouts || payload?.Layouts || payload?.data || payload?.Data || payload
        const layoutRecord = Array.isArray(layouts) ? layouts[0] : layouts
        const rawConfig = layoutRecord?.layoutConfig || layoutRecord?.LayoutConfig || layoutRecord
        let layoutData = rawConfig
        if (typeof rawConfig === 'string') {
          try { layoutData = JSON.parse(rawConfig) } catch { layoutData = null }
        }

        // Only set custom layout if we got valid data
        if (layoutData && typeof layoutData === 'object' && Object.keys(layoutData).length > 0) {
          setCustomLayout(layoutData)
        } else {
          setCustomLayout(null)
        }
      } catch (err) {
        // 404 or 400 — no custom layout found. This is expected, fall through to template.
        const status = err?.response?.status
        if (status !== 404 && status !== 400) {
          console.warn('[GridEngine] Failed to fetch custom layout, falling back to template:', err.message)
        }
        setApiError(true)
        setCustomLayout(null)
      } finally {
        setLoading(false)
      }
    }

    fetchCustomLayout()
  }, [enabled, gameType, editMode, gridLayoutSelection])

  const resolved = useMemo(() => {
    // Legacy V2 layouts are dedicated JSX shells. Allowing the GridEngine to
    // resolve a template here makes non-classic games fall back to the classic
    // V2 grid template and renders duplicate modern hero sections.
    if (DEDICATED_JSX_LAYOUT_STYLES.has(layoutStyle)) {
      return { gridConfig: null, isGridEngine: false, errors: [] }
    }

    if (!enabled) {
      return { gridConfig: null, isGridEngine: false, errors: [] }
    }

    // If user selected 'default' in Appearance panel → use legacy JSX
    if (gridLayoutSelection === 'default') {
      return { gridConfig: null, isGridEngine: false, errors: [] }
    }

    // While loading API data, don't show anything yet (avoid flash)
    if (loading && !customLayout && !apiError) {
      return { gridConfig: null, isGridEngine: false, errors: [] }
    }

    // 1. Custom layout from API (Phase 2)
    if (customLayout && !apiError) {
      const migrated = migrateConfig(customLayout)
      const errors = validateGridLayout(migrated)
      if (errors.length === 0) {
        return { gridConfig: migrated, isGridEngine: true, errors: [] }
      }
      console.warn('[GridEngine] Custom layout validation failed:', errors)
    }

    // 2. Grid layout selection from Appearance panel (hero-sidebar, magazine, stack, dashboard, mosaic)
    //    Dynamically redistribute sections from the current layout style's template
    if (gridLayoutSelection && gridLayoutSelection !== 'default') {
      // Get the source template from the current layout style
      const sourceTemplate = getTemplate(layoutStyle, gameType)
      if (sourceTemplate) {
        const redistributed = redistributeSections(sourceTemplate, gridLayoutSelection)
        if (redistributed) {
          return { gridConfig: redistributed, isGridEngine: true, errors: [] }
        }
      }
      // Fallback: try static template lookup if redistribution didn't work
      const staticTemplate = getTemplate(gridLayoutSelection, gameType)
      if (staticTemplate) {
        return { gridConfig: staticTemplate, isGridEngine: true, errors: [] }
      }
    }

    // 3. Template matching layoutStyle
    const template = getTemplate(layoutStyle, gameType)
    if (template) {
      return { gridConfig: template, isGridEngine: true, errors: [] }
    }

    // 4. No match — fallback to legacy JSX
    return { gridConfig: null, isGridEngine: false, errors: [] }
  }, [enabled, layoutStyle, gameType, gridLayoutSelection, customLayout, apiError, loading])

  return {
    ...resolved,
    loading,
  }
}
